'use client';

import { useTransition } from 'react';
import { Button } from './Button';
import { FlexContainer } from './FlexContainer';

import ModalWrapper from './ModalWrapper';

import type { ModalWrapperProps } from './ModalWrapper';

export type ConfirmationModalProps = ModalWrapperProps & {
  onConfirm: () => Promise<void>;

  onCancel?: () => void;
  confirmButtonText?: string;
  cancelButtonText?: string;
  children?: React.ReactNode;
  destructive?: boolean;
  hideCancelButton?: boolean;
};

export default function ConfirmationModal({
  confirmButtonText = 'Confirm',
  cancelButtonText = 'Cancel',
  onConfirm,
  onCancel,
  children,
  destructive,
  hideCancelButton,
  ...modalWrapperProps
}: ConfirmationModalProps) {
  const [isPending, startTransition] = useTransition();

  function handleConfirm() {
    startTransition(async () => {
      try {
        await onConfirm();
      } catch {
        // Swallow rejection: an uncaught rejection escapes the transition and crashes the app
        // via the root error boundary. Keep modal open so the user can retry or cancel.
        return;
      }
      modalWrapperProps.onClose();
    });
  }

  function handleCancel() {
    onCancel?.();
    modalWrapperProps.onClose();
  }

  return (
    <ModalWrapper
      {...modalWrapperProps}
      footer={
        <FlexContainer className="gap-4">
          {!hideCancelButton && (
            <Button onClick={handleCancel} variant="quaternary">
              {cancelButtonText}
            </Button>
          )}
          <Button onClick={handleConfirm} pending={isPending} variant={destructive ? 'destructive' : 'primary'}>
            {confirmButtonText}
          </Button>
        </FlexContainer>
      }
      onClose={handleCancel}
    >
      {children}
    </ModalWrapper>
  );
}
